// Theme color names - used across avatars, birthday images, and event theming
export type ThemeColorName =
  | 'purple'
  | 'blue'
  | 'peach'
  | 'sand'
  | 'yellow'
  | 'green'
  | 'mint'
  | 'lavender'

// Theme color palettes - each color has multiple shades for different use cases
export const THEME_PALETTES: Record<ThemeColorName, {
  background: string    // Light background color (avatars, profile pictures)
  accent: string        // Accent color for text on light backgrounds
  gradient: string      // Darker color for gradients and overlays
}> = {
  purple: {
    background: '#E8D0F5',
    accent: '#7C3AED',
    gradient: '#9C7DAA',
  },
  blue: {
    background: '#B4DAFD',
    accent: '#00A2F1',
    gradient: '#5E7C9C',
  },
  peach: {
    background: '#F7B695',
    accent: '#C2410C',
    gradient: '#B67A5C',
  },
  sand: {
    background: '#FFE4DF',
    accent: '#E11D48',
    gradient: '#C7998F',
  },
  yellow: {
    background: '#FFF6DE',
    accent: '#D97706',
    gradient: '#D68F25',
  },
  green: {
    background: '#B4EAA7',
    accent: '#16A34A',
    gradient: '#758E70',
  },
  mint: {
    background: '#DEF9FF',
    accent: '#0891B2',
    gradient: '#8DACB3',
  },
  lavender: {
    background: '#FFDEFF',
    accent: '#C026D3',
    gradient: '#A88CA7',
  },
}

// Legacy exports for backwards compatibility
export type AvatarColorName = ThemeColorName

export const AVATAR_COLORS: Record<AvatarColorName, string> = {
  purple: THEME_PALETTES.purple.background,
  blue: THEME_PALETTES.blue.background,
  peach: THEME_PALETTES.peach.background,
  sand: THEME_PALETTES.sand.background,
  yellow: THEME_PALETTES.yellow.background,
  green: THEME_PALETTES.green.background,
  mint: THEME_PALETTES.mint.background,
  lavender: THEME_PALETTES.lavender.background,
}

// Preset colors array (for UI pickers)
export const PRESET_COLORS = [
  AVATAR_COLORS.purple,
  AVATAR_COLORS.blue,
  AVATAR_COLORS.peach,
  AVATAR_COLORS.sand,
  AVATAR_COLORS.yellow,
  AVATAR_COLORS.green,
  AVATAR_COLORS.mint,
  AVATAR_COLORS.lavender,
] as const

// Accent colors for text on avatar backgrounds
export const ACCENT_COLORS: Record<string, string> = {
  [AVATAR_COLORS.purple]: THEME_PALETTES.purple.accent,
  [AVATAR_COLORS.blue]: THEME_PALETTES.blue.accent,
  [AVATAR_COLORS.peach]: THEME_PALETTES.peach.accent,
  [AVATAR_COLORS.sand]: THEME_PALETTES.sand.accent,
  [AVATAR_COLORS.yellow]: THEME_PALETTES.yellow.accent,
  [AVATAR_COLORS.green]: THEME_PALETTES.green.accent,
  [AVATAR_COLORS.mint]: THEME_PALETTES.mint.accent,
  [AVATAR_COLORS.lavender]: THEME_PALETTES.lavender.accent,
}

// Gradient colors for birthday card overlays
export const GRADIENT_COLORS: Record<AvatarColorName, string> = {
  purple: THEME_PALETTES.purple.gradient,
  blue: THEME_PALETTES.blue.gradient,
  peach: THEME_PALETTES.peach.gradient,
  sand: THEME_PALETTES.sand.gradient,
  yellow: THEME_PALETTES.yellow.gradient,
  green: THEME_PALETTES.green.gradient,
  mint: THEME_PALETTES.mint.gradient,
  lavender: THEME_PALETTES.lavender.gradient,
}

// Map hex values back to color names
export const HEX_TO_COLOR_NAME: Record<string, AvatarColorName> = {
  '#E8D0F5': 'purple',
  '#B4DAFD': 'blue',
  '#F7B695': 'peach',
  '#FFE4DF': 'sand',
  '#FFF6DE': 'yellow',
  '#B4EAA7': 'green',
  '#DEF9FF': 'mint',
  '#FFDEFF': 'lavender',
}

export const DEFAULT_BACKGROUND_COLOR = AVATAR_COLORS.mint
export const DEFAULT_COLOR_NAME: AvatarColorName = 'mint'

// Get color name from hex value
export function getColorName (hexColor: string): AvatarColorName {
  return HEX_TO_COLOR_NAME[hexColor] || DEFAULT_COLOR_NAME
}

// Get gradient color from background color hex
export function getGradientColor (backgroundColorHex: string | null | undefined): string | null {
  if (!backgroundColorHex) return null
  const colorName = getColorName(backgroundColorHex)
  return GRADIENT_COLORS[colorName]
}
